import { userSettings } from '@/globals/userSettings'

const INLINE_TAGS = new Set([
  'B',
  'STRONG',
  'I',
  'U',
  'EM',
  'MARK',
  'SUB',
  'SUP',
  'INS',
  'DEL',
  'PRE',
  'ABBR',
  'CODE',
  'S',
  'TIME',
  'SPAN',
  'A',
])

function getFullSentence(node: Node): string {
  if (!node) return ''

  // If it's a text node, gather surrounding text from siblings
  if (node.nodeType === Node.TEXT_NODE) {
    return gatherSiblings(node)
  }

  // If it's an inline element, gather its text + siblings
  if (INLINE_TAGS.has(node.nodeName.toUpperCase())) {
    return gatherSiblings(node)
  }

  // Block-level or other: just read the element's textContent
  return (node.textContent ?? '').trim()
}

function gatherSiblings(node: Node): string {
  const parts: string[] = []

  // Walk backwards
  let prev: Node | null = node.previousSibling
  while (prev) {
    if (prev.nodeType === Node.TEXT_NODE || INLINE_TAGS.has(prev.nodeName.toUpperCase())) {
      const text = (prev.textContent ?? '').trim()
      if (text) parts.unshift(text)
    }
    prev = prev.previousSibling
  }

  // Current node
  parts.push((node.textContent ?? '').trim())

  // Walk forward
  let next: Node | null = node.nextSibling
  while (next) {
    if (next.nodeType === Node.TEXT_NODE || INLINE_TAGS.has(next.nodeName.toUpperCase())) {
      const text = (next.textContent ?? '').trim()
      if (text) parts.push(text)
    }
    next = next.nextSibling
  }

  return parts.filter(Boolean).join(' ')
}

function speakText(text: string) {
  if (!text) return
  if ('speechSynthesis' in window && 'SpeechSynthesisUtterance' in window) {
    speechSynthesis.cancel()
    const utter = new SpeechSynthesisUtterance(text)
    if (userSettings.lang) {
      utter.lang = userSettings.lang
    }
    speechSynthesis.speak(utter)
  }
}

let clickHandler: ((e: Event) => void) | null = null

export function enable() {
  if (clickHandler) return
  clickHandler = (event: Event) => {
    const target = event.target as Element
    if (target.tagName === 'BODY' || target.tagName === 'HTML' || target.closest('.asw-container')) return

    const sentence = getFullSentence(target)
    if (sentence) speakText(sentence)
  }
  document.addEventListener('click', clickHandler, true)
}

export function disable() {
  if (clickHandler) {
    document.removeEventListener('click', clickHandler, true)
    clickHandler = null
  }
  if ('speechSynthesis' in window) speechSynthesis.cancel()
}

export default function screenReader(enabled: boolean) {
  if (enabled) enable()
  else disable()
}
